/**
 * @author Matěj Grabovský <matej.grabovsky@ki-wi.cz>
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * Aktualizátor zařízení.
 */
class Updater {
  /**
   * Vytvoří aktualizátor.
   * @param {AppManager} appManager
   * @param {string} [buildId] Identifikátor sestavení.
   * @param {Communicator} communicator
   * @param {Session} session Sezení s nastavením zařízení.
   * @param {Store} store Datový sklad s přehrávaným obsahem.
   * @returns {Updater} Nový aktualizátor.
   */
  constructor(appManager, buildId, communicator, session, store) {
    this.appManager = appManager;
    this.buildId = buildId;
    this.communicator = communicator;
    this.session = session;
    this.store = store;
    this.updatePending = false;
  }

  /**
   * Obnoví nastavení aktualizátoru při startu zařízení.
   * @returns {Promise.<undefined>} Vyřeší se po obnovení aktualizátoru.
   */
  start() {
    const { session } = this;
    if (this.buildId === session.buildId) {
      // Pokud je identifikátor sestavení stejný jako při minulém spuštění, tak se nic nedělá.
      return Promise.resolve();
    }
    // Pokud je spuštěno jiné sestavení aplikace než minule, aktualizuje se nastavení aplikace.
    // TODO Store update undefined bug
    // - Nesmí zde dojít ke vložení položky s undefined, jinak se bug projeví.
    // - Metoda update nepřepíše v paměti položky s hodnotou undefined,
    //   ale v perzistentním úložišti ano.
    // - Před jejím voláním se data vloží přes inject.
    //   Zde je ale problém, že se dodržuje identifikátor z attrs a ne z parametrů.
    //   Zatím byl tedy inject níže zakomentovaný, dokud se nenajde lepší řešení.
    // - Asi přepracovat tak, aby se používalo {onConflict: 'replace'}
    const sessionId = session.id;
    const attrs = { buildId: this.buildId, id: sessionId, version: null };
    const { store } = this;
    // store.inject('session', attrs);
    return store.update('session', sessionId, attrs);
  }

  // TODO Nepoužívaná implementace pro změnu orientace přes update.
  // /**
  //  * Aktualizujce aplikaci na danou platformu bez ohledu na verzi.
  //  * @param {UpdatePlatform} updatePlatform Identifikátor platformy, na kterou se má aktualizovat.
  //  * @returns {Promise.<undefined>} Vyřeší se po dokončení aktualizace.
  //  */
  // updatePlatform (updatePlatform) {
  //   return new Promise((resolve) => {
  //     if (this.updatePending) {
  //       // Pokud už probíhá aktualizace, vyhodíme výjimku.
  //       throw new Error('An update is already pending.');
  //     }
  //     // Zablokují se další aktualizace.
  //     this.updatePending = true;
  //     const {session} = this;
  //     return resolve(
  //       // Detekce požadovaného formátu aplikace.
  //       this.appManager.getFormat()
  //       // Vyhledání vhodné aktualizace na serveru.
  //       .then((formatEnum) => this.communicator.update(session.serverUrl, session.deviceId, formatEnum, updatePlatform))
  //       .then((updateResult) => {
  //         // Pokračujeme v aktualizaci, pouze pokud máme co aktualizovat.
  //         if (!updateResult) {
  //           // Tato aktualizace je dokončená.
  //           return;
  //         }
  //         return (
  //           this.appManager.update(updateResult.url)
  //           .then(() => this.store.update('session', session.id, {
  //             // Identifikátor sestavení, aby šla detekovat změna verze z jiného zdroje, než je tento aktualizační proces.
  //             buildId: updateResult.buildId,
  //             // Načtená logická verze, aby se již příště opakovaně nestahovala.
  //             version: updateResult.version
  //           }))
  //           // Dokončí se aktualizace spuštěním aktualizované aplikace.
  //           .then(() => this.appManager.finishUpdate())
  //         );
  //       })
  //       .finally(() => {
  //         // Odblokují se další aktualizace.
  //         this.updatePending = false;
  //       })
  //     );
  //   });
  // }

  /**
   * Aktualizuje aplikaci na novou verzi.
   * @param {string} version Verze aplikace, na kterou aktualizovat.
   * @returns {Promise.<undefined>} Vyřeší se po dokončení aktualizace.
   */
  updateVersion(version) {
    return new Promise((resolve) => {
      const { session } = this;
      if (!this.buildId || version === session.version) {
        // Pokud není dostupné buildId (dev verze), tak se aktualizace neprovádí.
        // Pokud se verze aplikace nezměnila, také se aktualizace neprovádí.
        return resolve();
      }
      // Pokud už probíhá aktualizace, vyhodíme výjimku.
      if (this.updatePending) {
        throw new Error('An update is already pending.');
      }
      this.updatePending = true;
      // Provedení aktualizace aplikace.
      return resolve(
        // Detekce platformy na které je aplikace spuštěna.
        this.appManager
          .getUpdatePlatform()
          .then((updatePlatform) =>
            // Detekce požadovaného formátu aplikace.
            this.appManager
              .getFormat()
              // Vyhledání vhodné aktualizace na serveru.
              .then((formatEnum) =>
                this.communicator.update(
                  session.serverUrl,
                  session.deviceId,
                  formatEnum,
                  updatePlatform
                )
              )
          )
          .then((updateResult) => {
            // Pokračujeme v aktualizaci, pouze pokud máme co aktualizovat.
            if (!updateResult) {
              // Tato aktualizace je dokončená.
              return;
            }
            // Porovná se identifikátor sestavení spuštěné a aktualizované aplikace. Pokud jsou stejné, není třeba aktualizaci provádět.
            const buildIdMatched = this.buildId === updateResult.buildId;
            let updatePromise = Promise.resolve();
            if (!buildIdMatched) {
              // Pokud má aktualizace jiný identifikátor sestavení, tak se aplikace aktualizuje.
              updatePromise = updatePromise.then(() =>
                this.appManager.update(updateResult.url)
              );
            }
            // Uložení hodnot do persistentního datového skladu, pro porování při dalším spuštění.
            // TODO Store update undefined bug
            // - Nesmí zde dojít ke vložení položky s undefined, jinak se bug projeví.
            // - Metoda update nepřepíše v paměti položky s hodnotou undefined,
            //   ale v perzistentním úložišti ano.
            // - Před jejím voláním se data vloží přes inject.
            //   Zde je ale problém, že se dodržuje identifikátor z attrs a ne z parametrů.
            //   Zatím byl tedy inject níže zakomentovaný, dokud se nenajde lepší řešení.
            // - Asi přepracovat tak, aby se používalo {onConflict: 'replace'}
            const sessionId = session.id;
            const attrs = {
              id: sessionId,
              // Identifikátor sestavení, aby šla detekovat změna verze z jiného zdroje, než je tento aktualizační proces.
              buildId: updateResult.buildId,
              // Načtená logická verze, aby se již příště opakovaně nestahovala.
              version: updateResult.version
            };
            const { store } = this;
            // store.inject('session', attrs);
            updatePromise = updatePromise.then(() =>
              store.update('session', sessionId, attrs)
            );
            if (!buildIdMatched) {
              // Pokud má aktualizace jiný identifikátor sestavení, tak se zařízení po aktualizaci restartuje.
              // Dokončí se aktualizace spuštěním aktualizované aplikace.
              updatePromise = updatePromise.then(() =>
                this.appManager.finishUpdate()
              );
            }
            return updatePromise;
          })
          .finally(() => {
            // Odblokují se další aktualizace.
            this.updatePending = false;
          })
      );
    });
  }
}

export default Updater;
